import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CertificateCacheService, Certificate } from './certificate-cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl = `${environment.apiUrl}/auth`; // URL do endpoint de login

  constructor(
    private http: HttpClient,
    private certificateCache: CertificateCacheService
  ) {}

  /**
   * Realiza a autenticação do usuário enviando as credenciais para a API.
   * Se o login for bem-sucedido, a API retorna um token JWT.
   * @param email E-mail do usuário
   * @param password Senha do usuário
   * @returns Observable contendo a resposta da API
   */
  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/login`, { email, password }, { headers: { 'Content-Type': 'application/json' } }).pipe(catchError(this.handleError)); // Trata possíveis erros da requisição
  }

  /**
   * Manipula erros das requisições HTTP.
   * Identifica erros comuns e retorna uma mensagem adequada ao usuário.
   * @param error Objeto contendo detalhes do erro HTTP
   * @returns Lança um erro com uma mensagem apropriada
   */
  private handleError(error: HttpErrorResponse) {
    console.log('Erro recebido do servidor:', error); // <-- DEBUG

    let errorMessage = 'Ocorreu um erro desconhecido.';
    let code = error.error?.code || error.status || 0;

    // Tenta pegar a mensagem de erro diretamente da API
    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.status === 400) {
      errorMessage = 'Preencha todos os campos obrigatórios corretamente.';
    } else if (error.status === 401) {
      errorMessage = 'Usuário ou senha inválidos.';
    } else if (error.status === 409) {
      errorMessage = 'O e-mail informado já está cadastrado.';
    } else if (error.status === 500) {
      errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
    } else if (!navigator.onLine) {
      errorMessage = 'Sem conexão com a internet.';
    }

    console.log('Mensagem de erro tratada:', errorMessage);

    return throwError(() => ({
      status: error.status,
      code,
      message: errorMessage,
    }));
  }

  /**
   * Salva o token JWT no armazenamento local do navegador.
   * Esse token será usado para autenticar futuras requisições à API.
   * @param token Token JWT retornado pela API
   */
  saveToken(token: string): void {
    localStorage.setItem('token', token);
  }

  /**
   * Obtém o token armazenado no navegador.
   * Esse token será utilizado para validar a autenticação do usuário.
   * @returns Token JWT armazenado ou null se não existir
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Remove o token do armazenamento local.
   * Esse método é chamado durante o logout do usuário.
   */
  logout(): Observable<any> {
    return this.http
      .post(
        `${this.baseUrl}/logout`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      )
      .pipe(catchError(this.handleError));
  }

  logoutAndClear(): void {
    this.logout().subscribe({
      next: () => {
        localStorage.removeItem('token');
      },
      error: () => {
        // mesmo que a chamada falhe, garante a remoção local
        localStorage.removeItem('token');
      },
    });
  }

  /**
   * Verifica se o token armazenado é válido e não expirou.
   * O token JWT contém informações sobre o usuário e o tempo de expiração.
   * @returns true se o token for válido, false caso contrário
   */

  /**
   * Verifica se o usuário está autenticado com base no token armazenado.
   * O usuário será considerado autenticado se:
   * - O token estiver presente no armazenamento local.
   * - O token não estiver expirado (tempo de expiração não atingido).
   * @returns true se o usuário estiver autenticado, false caso contrário
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false; // Se não houver token, retorna falso

    const payload = this.decodeToken(token);
    if (!payload) return false; // Se não conseguir decodificar, retorna falso

    return Date.now() < payload.exp * 1000; // Retorna true se ainda estiver válido
  }

  /**
   * Decodifica o payload do token JWT e extrai seus dados.
   * O payload contém informações sobre o usuário e o tempo de expiração do token.
   * @param token Token JWT a ser decodificado
   * @returns Objeto contendo os dados do token ou null se a decodificação falhar
   */
  private decodeToken(token: string): any {
    try {
      const base64Payload = token.split('.')[1]; // Obtém a parte do payload do JWT
      const decoded = atob(base64Payload); // Decodifica de Base64 para JSON
      return JSON.parse(decoded); // Converte a string JSON em objeto
    } catch (e) {
      return null; // Retorna null se a decodificação falhar
    }
  }

  register(email: string, password: string, confirmPassword: string, token?: string, parentId?: string): Observable<any> {
    const payload: any = {
      email,
      password,
      confirm_password: confirmPassword,
    };

    if (token) payload.token = token;
    if (parentId) payload.parent_id = parentId;

    return this.http.post<any>(`${this.baseUrl}/register`, payload).pipe(catchError(this.handleError));
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/forgot-password`, { email }).pipe(catchError(this.handleError));
  }

  /**
   * Verifica se o código OTP informado é válido.
   * @param email E-mail do usuário
   * @param otp Código OTP de verificação enviado por e-mail
   * @returns Observable com a resposta da API
   */
  verifyOtp(email: string, otp: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/verify-otp`, { email, otp }).pipe(catchError(this.handleError));
  }

  /**
   * Redefine a senha do usuário utilizando um código OTP válido.
   * Envia os dados para a API, que valida o código e atualiza a senha.
   *
   * @param email E-mail do usuário que solicitou a redefinição
   * @param otp Código OTP recebido por e-mail
   * @param password Nova senha definida pelo usuário
   * @param confirmPassword Confirmação da nova senha
   * @returns Observable com a resposta da API (sucesso ou erro)
   */
  resetPassword(email: string, otp: string, password: string, confirmPassword: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/reset-password`, { email, otp, password, confirm_password: confirmPassword }).pipe(catchError(this.handleError));
  }

  getUserPicture(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = this.decodeToken(token);
    return payload?.picture || null;
  }

  getLoggedUserId(): number | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = this.decodeToken(token);
    return payload?.sub ? parseInt(payload.sub, 10) : null;
  }

  /**
   * Retorna a role do usuário a partir do JWT.
   * Aceita payload.role ou payload.role_name (backend pode usar qualquer um).
   * Retorna string normalizada (trim) ou null.
   */
  getUserRole(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = this.decodeToken(token);
    const raw = payload?.role ?? payload?.role_name ?? null;
    if (raw == null) return null;
    const value = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    return value === '' ? null : value;
  }

  // Métodos para gerenciar certificados
  getAccessToken(): string | null {
    return this.getToken();
  }

  getStoredCertificate(): Certificate | null {
    return this.certificateCache.getCachedCertificate();
  }

  storeCertificate(certificate: Certificate): void {
    this.certificateCache.storeCertificate(certificate);
  }

  hasValidCertificate(): boolean {
    return this.certificateCache.hasValidCertificate();
  }

  clearCertificate(): void {
    this.certificateCache.clearCertificate();
  }

  refreshToken(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/refresh`, {}, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getToken()}`
      }
    }).pipe(catchError(this.handleError));
  }

  /**
   * Verifica se o usuário tem certificado válido e token
   */
  isFullyAuthenticated(): boolean {
    return this.isAuthenticated() && this.hasValidCertificate();
  }

  /**
   * Limpa todos os dados de autenticação
   */
  clearAllAuthData(): void {
    localStorage.removeItem('token');
    this.clearCertificate();
  }
}
